import React, { useEffect, useRef, useState } from "react";
import UseContext from "hooks/useContext";
import { Check, ChevronDown } from "assets/icons";
import { NO_PERIMETER_CHOICE } from "components/perimeterMissionStep/perimeterChoice";

const PerimeterOption = ({ label, hint, isSelected, onSelect }) => (
    <li
        role="option"
        aria-selected={isSelected}
        onClick={onSelect}
        className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-3 ${
            isSelected ? "bg-yellow-50" : "hover:bg-gray-50"
        }`}
    >
        <div className="flex min-w-0 flex-col">
            <span className="truncate ft-b text-gray-900">{label}</span>
            {hint && <span className="ft-xs text-gray-500">{hint}</span>}
        </div>
        {isSelected && (
            <span className="shrink-0" aria-hidden="true">
                <Check wh={20} color="#ff9600" />
            </span>
        )}
    </li>
);

// Perimeter menu fed by GET company/perimeters/options. `fallbackLabel` names a
// value the options do not offer (an archived current perimeter). In a modal,
// `isListInline` keeps the list in the flow so the scrolling body cannot clip it.
const PerimeterSelect = ({
    options,
    value,
    onChange,
    fallbackLabel,
    isDisabled = false,
    isListInline = false,
    labelId,
}) => {
    const { language } = UseContext();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const perimeters = options?.perimeters || [];

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleMouseDown = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") setIsOpen(false);
        };
        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isDisabled) setIsOpen(false);
    }, [isDisabled]);

    const noPerimeterLabel = language.getValue(
        "perimeters.missionStep.noPerimeter"
    );
    const selectedLabel =
        value === NO_PERIMETER_CHOICE
            ? noPerimeterLabel
            : perimeters.find((perimeter) => perimeter.id === value)?.name ||
              fallbackLabel;

    const select = (choice) => {
        onChange(choice);
        setIsOpen(false);
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-labelledby={labelId}
                disabled={isDisabled}
                onClick={() => setIsOpen((open) => !open)}
                className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 text-left ft-b hover:bg-gray-50 focus:border-yellow-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50"
            >
                <span
                    className={`truncate ${
                        selectedLabel ? "text-gray-900" : "text-gray-400"
                    }`}
                >
                    {selectedLabel ||
                        language.getValue("perimeters.missionStep.placeholder")}
                </span>
                <span
                    aria-hidden="true"
                    className={`shrink-0 transition-transform ${
                        isOpen ? "rotate-180" : ""
                    }`}
                >
                    <ChevronDown wh={20} color="#374151" />
                </span>
            </button>
            {isOpen && (
                <ul
                    role="listbox"
                    className={`${
                        isListInline
                            ? "relative"
                            : "absolute left-0 top-full z-30"
                    } mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-gray-100 bg-white py-1 shadow-lg`}
                >
                    {perimeters.map((perimeter) => (
                        <PerimeterOption
                            key={perimeter.id}
                            label={perimeter.name}
                            isSelected={value === perimeter.id}
                            onSelect={() => select(perimeter.id)}
                        />
                    ))}
                    {options?.allowNone && (
                        <>
                            {perimeters.length > 0 && (
                                <li
                                    role="presentation"
                                    className="border-t border-gray-100"
                                />
                            )}
                            <PerimeterOption
                                label={noPerimeterLabel}
                                hint={language.getValue(
                                    "perimeters.missionStep.noPerimeterHint"
                                )}
                                isSelected={value === NO_PERIMETER_CHOICE}
                                onSelect={() => select(NO_PERIMETER_CHOICE)}
                            />
                        </>
                    )}
                </ul>
            )}
        </div>
    );
};

export default PerimeterSelect;
